import React, { useEffect, useMemo, useState } from "react";
import {
  Calendar,
  Clock,
  HousePlus,
  MapPin,
  Music,
  SquarePen,
  X,
} from "lucide-react";
import {
  AvailableBookingSlot,
  BookingCreationDto,
  useAvailableBookingSlotsQuery,
  useBookingMutation,
} from "../../api/bookingApi.ts";
import MonthCalendar from "../MonthCalendar.tsx";
import SlotTimePicker from "./SlotTimePicker.tsx";
import { format } from "date-fns";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  getTranslation,
  InstrumentWithFilters,
} from "../../api/instrumentApi.ts";
import {
  TeacherLessonPrice,
  TeacherLocation,
  useTeacherLessonPricesQuery,
  useTeacherLocationsQuery,
  useTeacherQuery,
} from "../../api/teacherApi.ts";
import { ProfilePicture } from "../profile/ProfilePicture.tsx";
import { Trans, useLingui } from "@lingui/react/macro";
import { LocationType } from "../../api/locationApi.ts";
import ActionButton from "../ActionButton.tsx";
import { useQueryNav } from "../../hooks/useQueryNav.ts";

interface BookingProps {
  teacherId: string;
  locationIdQuery: string | null;
  lessonTypeQuery: string | null;
  instrumentQuery: string | null;
}

const NewBooking: React.FC<BookingProps> = ({
  teacherId,
  locationIdQuery,
  lessonTypeQuery,
  instrumentQuery,
}) => {
  const { t } = useLingui();

  const [selectedDate, setSelectedDate] = useState<Date>();
  const [isDateInitialized, setIsDateInitialized] = useState(false);

  // Map from slot.from (ISO string) → slot, used for multi-slot selection
  const [selectedSlotsMap, setSelectedSlotsMap] = useState<
    Map<string, AvailableBookingSlot>
  >(new Map());

  const [selectedTeacherLessonPrice, setSelectedTeacherLessonPrice] =
    useState<TeacherLessonPrice>();
  const [selectedInstrument, setSelectedInstrument] =
    useState<InstrumentWithFilters>();
  const [selectedLocation, setSelectedLocation] = useState<TeacherLocation>();
  const [selectedLessonType, setSelectedLessonType] =
    useState<Exclude<LocationType, "BOTH">>("IN_PERSON");
  const [editingLocation, setEditingLocation] = useState(false);
  const [editingLessonType, setEditingLessonType] = useState(false);
  const [editingInstrument, setEditingInstrument] = useState(false);

  const { mutate: createBooking, isPending } = useBookingMutation();
  const { data: teacher } = useTeacherQuery(teacherId);
  const { data: locations } = useTeacherLocationsQuery(teacherId);
  const { language, dateFnsLocale } = useLanguageStore();
  const { data: teacherLessonPricesDto } = useTeacherLessonPricesQuery(
    teacherId,
    "BOOKING",
  );
  const { data: availableSlotsDto } = useAvailableBookingSlotsQuery(
    teacherId,
    selectedLocation?.id,
    selectedTeacherLessonPrice?.durationInMinutes,
  );
  const navigateTo = useQueryNav();

  // -------------------------------------------------------------------------
  // Derived
  // -------------------------------------------------------------------------

  const locationsWithInstruments = useMemo(
    () =>
      locations
        ? locations.locations.filter((l) => l.instruments.length > 0)
        : [],
    [locations],
  );

  const slotsByDate = useMemo<Record<string, AvailableBookingSlot[]>>(() => {
    const result: Record<string, AvailableBookingSlot[]> = {};
    for (const slot of availableSlotsDto?.slots ?? []) {
      const dateKey = format(new Date(slot.from), "yyyy-MM-dd");
      (result[dateKey] ??= []).push(slot);
    }
    return result;
  }, [availableSlotsDto]);

  const slotsByFrom = useMemo<Map<string, AvailableBookingSlot>>(() => {
    const map = new Map<string, AvailableBookingSlot>();
    for (const slot of availableSlotsDto?.slots ?? []) {
      map.set(slot.from, slot);
    }
    return map;
  }, [availableSlotsDto]);

  const availableForDate = useMemo<AvailableBookingSlot[]>(() => {
    if (!selectedDate) return [];
    return slotsByDate[format(selectedDate, "yyyy-MM-dd")] ?? [];
  }, [slotsByDate, selectedDate]);

  const slotOptions = useMemo(
    () =>
      availableForDate.map((s) => ({
        id: s.from,
        label: format(new Date(s.from), "HH:mm"),
      })),
    [availableForDate],
  );

  const selectedSlotsList = useMemo(
    () =>
      Array.from(selectedSlotsMap.values()).sort(
        (a, b) => new Date(a.from).getTime() - new Date(b.from).getTime(),
      ),
    [selectedSlotsMap],
  );

  const totalLessons = selectedSlotsMap.size;
  const totalPrice =
    selectedTeacherLessonPrice?.price != null
      ? selectedTeacherLessonPrice.price * totalLessons
      : null;

  const isBookingValid =
    selectedSlotsMap.size > 0 &&
    !!selectedInstrument &&
    !!selectedLocation &&
    !!selectedLessonType &&
    !!selectedTeacherLessonPrice;

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!selectedLocation) return;
    const supported =
      lessonTypeQuery &&
      (selectedLocation.locationType === "BOTH" ||
        selectedLocation.locationType.includes(lessonTypeQuery));
    if (supported) {
      setSelectedLessonType(lessonTypeQuery as Exclude<LocationType, "BOTH">);
    } else {
      setSelectedLessonType(
        selectedLocation.locationType === "BOTH"
          ? "IN_PERSON"
          : selectedLocation.locationType,
      );
    }
    setEditingLessonType(false);
  }, [lessonTypeQuery, selectedLocation]);

  // Navigate calendar to first available date when slots first load
  useEffect(() => {
    if (isDateInitialized || !availableSlotsDto) return;
    const firstDateKey = Object.keys(slotsByDate).sort()[0];
    if (firstDateKey) {
      setSelectedDate(new Date(firstDateKey));
      setIsDateInitialized(true);
    }
  }, [availableSlotsDto, isDateInitialized, slotsByDate]);

  useEffect(() => {
    setSelectedTeacherLessonPrice(teacherLessonPricesDto?.prices[0]);
  }, [teacherLessonPricesDto]);

  useEffect(() => {
    const locationFromQuery = locations?.locations.find(
      (l) => l.id === locationIdQuery,
    );
    if (locationFromQuery) {
      setSelectedLocation(locationFromQuery);
      setEditingLocation(false);
    } else {
      setEditingLocation(true);
    }
  }, [locationIdQuery, locations]);

  useEffect(() => {
    const instrument = selectedLocation?.instruments.find(
      (v) => v.name === instrumentQuery,
    );
    if (instrument) {
      setSelectedInstrument(instrument);
      setEditingInstrument(false);
    } else {
      setEditingInstrument(true);
    }
  }, [instrumentQuery, selectedLocation?.instruments]);

  useEffect(() => {
    setSelectedSlotsMap(new Map());
    setIsDateInitialized(false);
    setSelectedDate(undefined);
  }, [selectedTeacherLessonPrice]);

  useEffect(() => {
    setSelectedSlotsMap(new Map());
    setIsDateInitialized(false);
    setSelectedDate(undefined);
  }, [selectedLocation]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleToggleSlot = (slotFrom: string) => {
    const slot = slotsByFrom.get(slotFrom);
    if (!slot) return;
    setSelectedSlotsMap((prev) => {
      const next = new Map(prev);
      if (next.has(slotFrom)) {
        next.delete(slotFrom);
      } else {
        next.set(slotFrom, slot);
      }
      return next;
    });
  };

  const handleBooking = () => {
    if (selectedSlotsMap.size === 0 || !selectedInstrument) return;
    const bookingCreationDto: BookingCreationDto = {
      teacherId,
      instrument: selectedInstrument.name,
      locationId: selectedLocation?.id || "",
      timeSlots: selectedSlotsList.map((s) => ({
        from: new Date(s.from),
        to: new Date(s.to),
      })),
    };
    createBooking(bookingCreationDto, {
      onSuccess: (newBooking) => navigateTo(`/checkout/${newBooking.id}`),
      onError: () => console.error("error while booking lesson"),
    });
  };

  // -------------------------------------------------------------------------
  // UI helpers
  // -------------------------------------------------------------------------

  const getAddress = (location: TeacherLocation) =>
    `${location.countryCode}, ${location.city}`;

  const hasRemote = (location?: TeacherLocation) =>
    !!location && location.locationType !== "IN_PERSON";

  function getLessonTypeButton(
    lessonType: Exclude<LocationType, "BOTH">,
    label: string,
    description: string,
  ) {
    return (
      <button
        key={lessonType}
        onClick={() => {
          setSelectedLessonType(lessonType);
          setEditingLessonType(false);
        }}
        className={`flex-1 rounded-lg border p-3 text-left transition-colors ${
          selectedLessonType === lessonType
            ? "border-soluud-primary bg-soluud-info"
            : "border-gray-300 hover:border-gray-400"
        }`}
      >
        <div className="font-medium">{label}</div>
        <div className="mt-1 text-xs text-gray-500">{description}</div>
      </button>
    );
  }

  function getLessonTypesFromLocation(location: TeacherLocation | undefined) {
    if (!location) {
      return <Trans>Select a location to view possible lesson types.</Trans>;
    }
    const inPerson = getLessonTypeButton(
      "IN_PERSON",
      t`In Person`,
      t`Face-to-face lesson at the location`,
    );
    const remote = getLessonTypeButton(
      "REMOTE",
      t`Remote`,
      t`Online lesson via video call`,
    );
    switch (location.locationType) {
      case "BOTH":
        return (
          <div className="flex flex-col gap-3 sm:flex-row">
            {inPerson} {remote}
          </div>
        );
      case "IN_PERSON":
        return inPerson;
      case "REMOTE":
        return remote;
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div
      className={`mx-auto max-w-3xl p-4 sm:p-6 ${isBookingValid ? "pb-24 md:pb-6" : ""}`}
    >
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-soluud-text-primary text-2xl font-bold sm:text-3xl">
          <Trans>Book Your Music Lesson</Trans>
        </h1>
        {teacher && (
          <div className="mt-3 flex items-center gap-2.5">
            <div className="ring-soluud-primary/10 h-8 w-8 shrink-0 overflow-hidden rounded-full ring-2">
              <ProfilePicture
                profilePictureUrl={teacher.profilePictureUrl}
                firstName={teacher.firstName}
                lastName={teacher.lastName}
                className="h-full w-full"
                iconSize={14}
              />
            </div>
            <span className="text-sm font-medium text-gray-700">
              {teacher.firstName} {teacher.lastName}
            </span>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* 1. Duration — shown first as it gates availability fetching */}
        <div>
          <label className="mb-3 block text-sm font-medium text-gray-700">
            <Clock className="mr-1.5 inline h-4 w-4" />
            <Trans>Lesson Duration</Trans>
          </label>
          <div className="flex flex-wrap gap-2">
            {teacherLessonPricesDto?.prices.map((price) => (
              <button
                key={price.durationInMinutes}
                onClick={() => setSelectedTeacherLessonPrice(price)}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  selectedTeacherLessonPrice?.durationInMinutes ===
                  price.durationInMinutes
                    ? "border-soluud-primary bg-soluud-primary text-white"
                    : "hover:border-soluud-primary hover:bg-soluud-primary-subtle border-gray-300 text-gray-700"
                }`}
              >
                {price.durationInMinutes} <Trans>min</Trans>
              </button>
            ))}
          </div>
        </div>

        {/* 2. Location */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            <MapPin className="mr-1.5 inline h-4 w-4" />
            <Trans>Location</Trans>
          </label>
          {editingLocation ? (
            <div className="space-y-2">
              {locationsWithInstruments.map((location) => (
                <button
                  key={location.id}
                  onClick={() => {
                    setSelectedLocation(location);
                    setEditingLocation(false);
                  }}
                  className={`w-full rounded-lg border p-3 text-left transition-colors ${
                    selectedLocation?.id === location.id
                      ? "border-soluud-primary bg-soluud-info"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{location.name}</div>
                      <div className="mt-0.5 text-sm text-gray-500">
                        {getAddress(location)}
                      </div>
                    </div>
                    {hasRemote(location) && (
                      <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                        <Trans>Remote</Trans>
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="border-soluud-primary bg-soluud-primary-subtle rounded-full border px-3 py-1 text-sm font-medium">
                {selectedLocation?.city}, {selectedLocation?.name}
              </span>
              <button
                onClick={() => setEditingLocation(true)}
                className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <SquarePen className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* 3. Instrument */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            <Music className="mr-1.5 inline h-4 w-4" />
            <Trans>Instrument</Trans>
          </label>
          {editingInstrument ? (
            <div className="flex flex-wrap gap-2">
              {selectedLocation?.instruments.map((instrument) => (
                <button
                  key={instrument.name}
                  onClick={() => {
                    setSelectedInstrument(instrument);
                    setEditingInstrument(false);
                  }}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                    selectedInstrument?.name === instrument.name
                      ? "border-soluud-primary bg-soluud-primary text-white"
                      : "border-gray-300 text-gray-700 hover:border-gray-400"
                  }`}
                >
                  {getTranslation(instrument, language)}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="border-soluud-primary bg-soluud-primary-subtle rounded-full border px-3 py-1 text-sm font-medium">
                {selectedInstrument ? (
                  getTranslation(selectedInstrument, language)
                ) : (
                  <Trans>Unknown</Trans>
                )}
              </span>
              <button
                onClick={() => setEditingInstrument(true)}
                className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <SquarePen className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* 4. Lesson Type */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            <HousePlus className="mr-1.5 inline h-4 w-4" />
            <Trans>Lesson Type</Trans>
          </label>
          {editingLessonType ? (
            getLessonTypesFromLocation(selectedLocation)
          ) : (
            <div className="flex items-center gap-2">
              <span className="border-soluud-primary bg-soluud-primary-subtle rounded-full border px-3 py-1 text-sm font-medium">
                {selectedLessonType === "IN_PERSON" ? (
                  <Trans>In Person</Trans>
                ) : (
                  <Trans>Remote</Trans>
                )}
              </span>
              <button
                onClick={() => setEditingLessonType(true)}
                className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <SquarePen className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        <div className="border-t border-gray-100" />

        {/* 5. Date + Time */}
        <div>
          <label className="mb-3 block text-sm font-medium text-gray-700">
            <Calendar className="mr-1.5 inline h-4 w-4" />
            <Trans>Pick a date and time</Trans>
            {!selectedTeacherLessonPrice && (
              <span className="ml-2 text-xs font-normal text-gray-400">
                — <Trans>select a duration first</Trans>
              </span>
            )}
          </label>

          {selectedTeacherLessonPrice ? (
            <div className="flex flex-col gap-6 lg:flex-row">
              {/* Calendar — its natural height drives the row height on desktop */}
              <div className="lg:w-72 lg:shrink-0">
                <MonthCalendar
                  availableDates={new Set(Object.keys(slotsByDate))}
                  selectedDay={selectedDate}
                  onDayClick={(day: Date) => setSelectedDate(day)}
                  onlyFutureSlots={true}
                  focusMonth={selectedDate}
                />
              </div>

              {/* Time picker.
                  On desktop: absolutely positioned so it never contributes to
                  the flex row height — the panel height is always the calendar's.
                  On mobile: normal flow with a min-h floor. */}
              <div className="relative min-h-[14rem] flex-1">
                <div className="flex flex-col gap-3 lg:absolute lg:inset-0 lg:overflow-y-auto">
                  {selectedDate ? (
                    slotOptions.length > 0 ? (
                      <>
                        <p className="shrink-0 text-sm font-medium text-gray-600">
                          {format(selectedDate, "EEEE, d MMMM", {
                            locale: dateFnsLocale,
                          })}
                        </p>
                        <SlotTimePicker
                          slots={slotOptions}
                          selectedIds={new Set(selectedSlotsMap.keys())}
                          onToggle={handleToggleSlot}
                        />
                      </>
                    ) : (
                      <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 py-10">
                        <p className="text-sm text-gray-400">
                          <Trans>No available slots on this day.</Trans>
                        </p>
                      </div>
                    )
                  ) : (
                    <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 py-10">
                      <p className="text-sm text-gray-400">
                        <Trans>Select a date to see available times.</Trans>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-24 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-400">
                <Trans>
                  Choose a lesson duration above to see availability.
                </Trans>
              </p>
            </div>
          )}
        </div>

        {/* 6. Selected lessons chips */}
        {selectedSlotsList.length > 0 && (
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">
              <Trans>Selected lessons</Trans>
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedSlotsList.map((slot) => (
                <div
                  key={slot.from}
                  className="border-soluud-primary bg-soluud-primary-subtle text-soluud-text-primary flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium"
                >
                  <span>
                    {format(new Date(slot.from), "EEE d MMM", {
                      locale: dateFnsLocale,
                    })}
                  </span>
                  <span className="text-gray-400">·</span>
                  <span>{format(new Date(slot.from), "HH:mm")}</span>
                  <button
                    onClick={() => handleToggleSlot(slot.from)}
                    className="hover:text-soluud-text-primary ml-0.5 rounded-full p-0.5 text-gray-500 hover:bg-gray-200"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 7. Price + Book button */}
        <div>
          <div className="border-soluud-primary bg-soluud-primary-subtle flex items-center justify-between rounded-xl border p-4">
            <div>
              <p className="font-semibold">
                <Trans>Total</Trans>
              </p>
              <p className="mt-0.5 text-sm text-gray-500">
                {selectedTeacherLessonPrice?.durationInMinutes ?? "—"}{" "}
                <Trans>min</Trans>
                {" · "}
                {selectedLessonType === "IN_PERSON" ? (
                  <Trans>in person</Trans>
                ) : (
                  <Trans>remote</Trans>
                )}
                {totalLessons > 1 && (
                  <>
                    {" · "}
                    {totalLessons} <Trans>lessons</Trans>
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-2xl font-bold">
                {totalPrice != null ? `€${totalPrice.toFixed(2)}` : "—"}
              </span>
              <ActionButton
                type="button"
                disabled={!isBookingValid}
                onClick={handleBooking}
                isLoading={isPending}
              >
                <Trans>Book Lesson</Trans>
              </ActionButton>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky action bar — visible on small screens once a slot is selected */}
      {isBookingValid && (
        <div className="fixed right-0 bottom-0 left-0 z-30 border-t border-gray-200 bg-white px-4 py-3 shadow-lg md:hidden">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-4">
            <div>
              <div className="text-soluud-text-primary text-lg font-bold">
                {totalPrice != null ? `€${totalPrice.toFixed(2)}` : "—"}
              </div>
              <div className="text-xs text-gray-500">
                {totalLessons === 1 ? (
                  <Trans>1 lesson</Trans>
                ) : (
                  <Trans>{totalLessons} lessons</Trans>
                )}
              </div>
            </div>
            <ActionButton
              type="button"
              onClick={handleBooking}
              isLoading={isPending}
            >
              <Trans>Book Lesson</Trans>
            </ActionButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewBooking;
